import { Link, useRouterState } from "@tanstack/react-router";
import { useEffect, useState } from "react";

import { useCustomerLocale } from "@/hooks/use-customer-locale";

import { Button } from "@/components/ui/button";
import { readCookiePrefs, writeCookiePrefs } from "@/lib/cookie-consent";
import { applyConsent } from "@/lib/tracking";

const COPY = {
  nl: {
    label: "Cookievoorkeuren",
    text: "Noodzakelijke cookies zijn altijd actief. Optioneel ook analytische cookies.",
    more: "Meer info",
    deny: "Weigeren",
    allow: "Toestaan",
    settings: "Instellen",
  },
  en: {
    label: "Cookie preferences",
    text: "Essential cookies are always on. Analytics cookies are optional.",
    more: "More info",
    deny: "Decline",
    allow: "Allow",
    settings: "Settings",
  },
} as const;

/**
 * Cookiebanner die alleen verschijnt zolang de bezoeker nog geen keuze heeft
 * gemaakt. Pas na hydratatie lezen we localStorage, zodat SSR en client gelijk
 * blijven. Op de Engelse spiegel (/en/...) is de tekst Engels.
 */
export function CookieBanner() {
  const [visible, setVisible] = useState(false);
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const { locale } = useCustomerLocale();
  const sharedCustomerPage = /^\/(mijn-account|mijn-ritten|mijn-gegevens|factuurgegevens|rit\/|factuur\/|betalen\/|reset-password)/.test(pathname);
  const t = (sharedCustomerPage && locale === "en") || pathname === "/en" || pathname.startsWith("/en/") ? COPY.en : COPY.nl;

  useEffect(() => {
    if (readCookiePrefs() === null) setVisible(true);
  }, []);

  if (!visible) return null;

  function choose(analytics: boolean) {
    writeCookiePrefs({ functional: analytics, analytics });
    applyConsent({ analytics });
    setVisible(false);
  }

  return (
    <div
      role="dialog"
      aria-label={t.label}
      className="fixed inset-x-0 bottom-0 z-[60] border-t border-border bg-background/98 backdrop-blur"
    >
      <div className="mx-auto flex max-w-5xl flex-col items-center gap-2 px-4 py-2 sm:flex-row sm:justify-between">
        <p className="text-xs text-muted-foreground sm:text-sm">
          {t.text}{" "}
          <Link to={t === COPY.en ? "/en/cookies" : "/cookies"} className="font-semibold text-primary underline">
            {t.more}
          </Link>
        </p>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            size="sm"
            className="h-8 text-xs font-semibold"
            onClick={() => choose(false)}
          >
            {t.deny}
          </Button>
          <Button size="sm" className="h-8 text-xs font-semibold" onClick={() => choose(true)}>
            {t.allow}
          </Button>
          <Button asChild variant="ghost" size="sm" className="h-8 text-xs font-semibold">
            <Link to="/cookie-instellingen" onClick={() => setVisible(false)}>
              {t.settings}
            </Link>
          </Button>
        </div>
      </div>
    </div>
  );
}
